// src/redux/slices/recoveryChatSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { recoveryService } from "../../services/api";

interface ChatMessage {
  id: number;
  recovery_request_id: number;
  user_id: number | null;
  admin_id: number | null;
  message: string;
  is_admin: boolean;
  attachments: any[] | null;
  read_at: string | null;
  created_at: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  admin?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  tempId?: number;
}

interface RecoveryChatState {
  chats: ChatMessage[];
  currentRecoveryId: number | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  unreadCount: number;
  caseUnreadCounts: Record<number, number>;
  lastChecked: string | null;
}

const initialState: RecoveryChatState = {
  chats: [],
  currentRecoveryId: null,
  isLoading: false,
  error: null,
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 50,
    total: 0,
  },
  unreadCount: 0,
  caseUnreadCounts: {},
  lastChecked: null,
};

// Update the getRecoveryChats thunk to fetch all pages
export const getRecoveryChats = createAsyncThunk(
  "recoveryChats/get",
  async (
    { recoveryId, page = 1, loadAll = false }: { recoveryId: number; page?: number; loadAll?: boolean },
    { rejectWithValue }
  ) => {
    try {
      if (loadAll) {
        // Load all pages
        let allChats: any[] = [];
        let currentPage = 1;
        let hasMore = true;
        let totalPages = 1;

        while (hasMore) {
          const response = await recoveryService.getChats(recoveryId, currentPage);
          allChats = [...allChats, ...response.data];
          
          totalPages = response.last_page;
          hasMore = currentPage < totalPages;
          currentPage++;
          
          // Optional: Add a small delay to avoid overwhelming the server
          if (hasMore) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        return {
          data: allChats,
          current_page: 1,
          last_page: 1,
          per_page: allChats.length,
          total: allChats.length,
          recoveryId,
          isComplete: true,
        };
      } else {
        // Original paginated approach
        const response = await recoveryService.getChats(recoveryId, page);
        return { ...response, recoveryId, isComplete: false };
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to load chat messages"
      );
    }
  }
);

export const sendRecoveryMessage = createAsyncThunk(
  "recoveryChats/send",
  async (
    {
      recoveryId,
      message,
      attachments,
    }: { recoveryId: number; message: string; attachments?: File[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await recoveryService.sendMessage(recoveryId, {
        message,
        attachments,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to send message"
      );
    }
  }
);

export const getUnreadChatCount = createAsyncThunk(
  "recoveryChats/getUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await recoveryService.getUnreadCount();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to get unread count"
      );
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  "recoveryChats/markAllRead",
  async (recoveryId: number, { rejectWithValue }) => {
    try {
      const response = await recoveryService.markAllAsRead(recoveryId);
      return { ...response, recoveryId };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to mark messages as read"
      );
    }
  }
);

const recoveryChatSlice = createSlice({
  name: "recoveryChats",
  initialState,
  reducers: {
    clearChatError: (state) => {
      state.error = null;
    },
    setCurrentRecovery: (state, action) => {
      state.currentRecoveryId = action.payload;
    },
    clearChats: (state) => {
      state.chats = [];
      state.currentRecoveryId = null;
      state.pagination = {
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: 0,
      };
    },
    addMessage: (state, action) => {
      const newMessage = action.payload;

      // Create unique identifier
      const messageId = newMessage.id || newMessage.tempId;
      if (!messageId) {
        console.error("Message has no ID, skipping");
        return;
      }

      // Check if message already exists by ID or tempId
      const existingIndex = state.chats.findIndex(
        (chat) =>
          chat.id === newMessage.id ||
          (chat.tempId &&
            newMessage.tempId &&
            chat.tempId === newMessage.tempId)
      );

      if (existingIndex === -1) {
        // Add new message
        state.chats.push(newMessage);

        // Sort by created_at
        state.chats.sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        // Update unread count if message is from other party
        const userStr = localStorage.getItem("user");
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            const isCurrentUser = newMessage.is_admin
              ? user.role === "admin" && newMessage.admin_id === user.id
              : !newMessage.is_admin && newMessage.user_id === user.id;

            if (!isCurrentUser) {
              state.unreadCount = (state.unreadCount || 0) + 1;
              state.caseUnreadCounts[newMessage.recovery_request_id] =
                (state.caseUnreadCounts[newMessage.recovery_request_id] || 0) +
                1;
            }
          } catch (error) {
            console.error("Error parsing user from localStorage:", error);
          }
        }
      } else {
        // Update existing message (replace optimistic with real)
        console.log("🔄 Updating existing message with real data");
        state.chats[existingIndex] = {
          ...state.chats[existingIndex],
          ...newMessage,
          // Preserve tempId for matching
          tempId: state.chats[existingIndex].tempId,
          // Ensure read_at is preserved if already set
          read_at: state.chats[existingIndex].read_at || newMessage.read_at,
        };
      }
    },
    updateUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
    updateCaseUnreadCount: (state, action) => {
      const { caseId, count } = action.payload;
      state.caseUnreadCounts[caseId] = count;
    },
    resetUnreadCountForCase: (state, action) => {
      const caseId = action.payload;
      const oldCount = state.caseUnreadCounts[caseId] || 0;
      state.unreadCount = Math.max(0, state.unreadCount - oldCount);
      delete state.caseUnreadCounts[caseId];
    },
    setLastChecked: (state, action) => {
      state.lastChecked = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRecoveryChats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getRecoveryChats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.chats = action.payload.data;
        state.pagination = {
          current_page: action.payload.current_page,
          last_page: action.payload.last_page,
          per_page: action.payload.per_page,
          total: action.payload.total,
        };
        state.currentRecoveryId = action.payload.recoveryId;
      })
      .addCase(getRecoveryChats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(sendRecoveryMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendRecoveryMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        // The message will be added via Pusher or the addMessage reducer
      })
      .addCase(sendRecoveryMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getUnreadChatCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload.unread_count || 0;
        state.lastChecked = new Date().toISOString();
      })
      .addCase(markAllAsRead.fulfilled, (state, action) => {
        const caseId = action.payload.recoveryId;
        // Update local chats to mark as read
        state.chats = state.chats.map((chat) => ({
          ...chat,
          read_at: chat.read_at || new Date().toISOString(),
        }));
        // Reset unread count for this case
        const oldCount = state.caseUnreadCounts[caseId] || 0;
        state.unreadCount = Math.max(0, state.unreadCount - oldCount);
        delete state.caseUnreadCounts[caseId];
      });
  },
});

export const {
  clearChatError,
  setCurrentRecovery,
  clearChats,
  addMessage,
  updateUnreadCount,
  updateCaseUnreadCount,
  resetUnreadCountForCase,
  setLastChecked,
} = recoveryChatSlice.actions;
export default recoveryChatSlice.reducer;
